import { useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  MessageSquare,
  PhoneForwarded,
  Phone,
  Save,
  ChevronLeft,
  ChevronRight,
  Users,
  TrendingUp,
  CheckCircle2,
  Percent,
  Download,
} from 'lucide-react';
import { Toast } from '@/components/common/Toast';
import { clickToCallApi } from '@/api/clickToCall.api';
import { exportToExcel, type ExportColumn } from '@/utils/exportToExcel';
import { fetchAllForExport, EXPORT_PAGE_SIZE } from '@/utils/fetchAllForExport';
import type {
  FollowupStatus,
  MarketingInterest,
  MarketingLead,
  MarketingLeadsResponse,
  UpdateRemarksRequest,
} from '../types';
import {
  INTEREST_OPTIONS,
  MARKETING_STAGE_OPTIONS,
  FOLLOWUP_STATUS_OPTIONS,
  followupLabel,
  followupVariant,
  getAgentId,
  fetchMarketingLeads,
  updateCallRemarks,
  extractApiError,
} from '../services/marketingApi';
import './CallsTable.css';
import './MarketingLeadsTable.css';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

type BadgeVariant = 'success' | 'danger' | 'warning' | 'neutral' | 'info' | 'deep-green';

function Badge({ text, variant }: { text: string; variant: BadgeVariant }) {
  return <span className={`badge badge-${variant}`}>{text}</span>;
}

/** Coerce interest_percentage (may be a string) to a number. */
function toNumber(v: number | string | null | undefined): number | null {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

/** Render seconds as m:ss. */
function formatDuration(seconds: number | null): string {
  if (seconds == null) return '-';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Format an IST timestamp WITHOUT applying the browser's timezone.
 * We read the wall-clock components straight from the string.
 */
function formatIST(value: string | null): string {
  if (!value) return '-';
  const m = value.match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!m) return value;
  const [, y, mo, d, hh, mm] = m;
  return `${parseInt(d, 10)} ${MONTHS[parseInt(mo, 10) - 1]} ${y}, ${hh}:${mm}`;
}

function humanizeStage(stage: string): string {
  if (!stage) return '-';
  const opt = MARKETING_STAGE_OPTIONS.find((o) => o.value === stage);
  if (opt) return opt.label;
  return stage.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Build a click-to-call customer number: country code (91) + 10-digit national number. */
function toCustomerNumber(phone: string | null): string | null {
  const digits = (phone || '').replace(/\D/g, '');
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 12 && digits.startsWith('91')) return digits;
  if (digits.length > 10) return `91${digits.slice(-10)}`;
  return null;
}

interface MarketingLeadRowProps {
  lead: MarketingLead;
  onLeadUpdated: (lead: MarketingLead) => void;
  notify: (message: string) => void;
}

function MarketingLeadRow({ lead, onLeadUpdated, notify }: MarketingLeadRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [remarkText, setRemarkText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>(lead.followup_status ?? '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [calling, setCalling] = useState(false);

  // Keep the status dropdown in sync when the underlying lead changes (e.g. after a save/refetch).
  useEffect(() => {
    setSelectedStatus(lead.followup_status ?? '');
  }, [lead.followup_status, lead.call_uuid]);

  const pct = toNumber(lead.interest_percentage);
  const actioned = lead.descriptions != null && lead.descriptions.trim() !== '';

  const remarkLines = (lead.descriptions ?? '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  const currentStatus = lead.followup_status ?? '';
  const hasRemark = remarkText.trim().length > 0;
  const statusChanged = selectedStatus !== '' && selectedStatus !== currentStatus;
  const canSave = (hasRemark || statusChanged) && !saving;

  const handleSave = async () => {
    if (!hasRemark && !statusChanged) return;
    setSaving(true);
    setSaveError(null);
    try {
      const body: UpdateRemarksRequest = { call_uuid: lead.call_uuid };
      if (hasRemark) body.remarks = remarkText.trim();
      if (statusChanged) body.followup_status = selectedStatus as FollowupStatus;
      const agentId = getAgentId();
      if (agentId) body.agent_id = agentId;

      const res = await updateCallRemarks(body);
      onLeadUpdated(res.lead);
      setRemarkText('');
      setSelectedStatus(res.lead.followup_status ?? '');
      notify('Lead updated');
    } catch (err) {
      setSaveError(extractApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleCall = async () => {
    // Always use the logged-in agent's ID so the agent's own phone rings.
    const agentId = localStorage.getItem('agent_id') || localStorage.getItem('agentId');
    if (!agentId) {
      notify('Agent ID not found');
      return;
    }
    const customerNumber = toCustomerNumber(lead.phone_number);
    if (!customerNumber) {
      notify('Customer phone number not available');
      return;
    }
    setCalling(true);
    try {
      const res = await clickToCallApi.clickToCall({
        agent_id: agentId,
        customer_number: customerNumber,
        cp_team: 'sales_team',
        call_type: 'phone',
      });
      notify(res.message || 'Call initiated successfully');
    } catch {
      notify('Failed to initiate call');
    } finally {
      setCalling(false);
    }
  };

  return (
    <>
      <tr className="call-row" onClick={() => setExpanded(!expanded)}>
        <td>
          <span className="expand-icon">
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </span>
        </td>
        <td className="phone-cell">
          <div className="mkt-phone-cell">
            <span>{lead.phone_number}</span>
            <button
              type="button"
              className={`mkt-call-btn ${calling ? 'calling' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleCall();
              }}
              disabled={calling}
              title="Call customer"
            >
              <Phone size={13} />
              {calling ? 'Calling…' : 'Call'}
            </button>
          </div>
        </td>
        <td>{humanizeStage(lead.stage)}</td>
        <td>
          {pct != null ? (
            <div className="mkt-interest">
              <span className="mkt-interest-val">{pct}%</span>
              <span className="mkt-interest-bar">
                <span
                  className="mkt-interest-fill"
                  style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
                />
              </span>
            </div>
          ) : (
            '-'
          )}
        </td>
        <td>{formatDuration(lead.call_duration)}</td>
        <td className="time-cell">{formatIST(lead.created_at)}</td>
        <td>
          <Badge text={followupLabel(lead.followup_status)} variant={followupVariant(lead.followup_status)} />
        </td>
        <td>
          {actioned ? (
            <span className="mkt-note-icon" title="Has remarks">
              <MessageSquare size={16} />
            </span>
          ) : (
            '-'
          )}
        </td>
      </tr>

      {expanded && (
        <tr className="expanded-row">
          <td colSpan={8}>
            <div className="expanded-content">
              <div className="mkt-detail-meta">
                {lead.call_status && (
                  <span>
                    Call Status: <Badge text={lead.call_status} variant="neutral" />
                  </span>
                )}
                {lead.transfer_triggered && (
                  <span className="mkt-transfer-flag">
                    <PhoneForwarded size={13} /> Transfer attempted
                  </span>
                )}
                <span className="mkt-meta-time">{formatIST(lead.created_at)} IST</span>
              </div>

              <div className="expanded-section expanded-section-wide mkt-remarks-section">
                <h4>
                  <MessageSquare size={16} /> Remarks
                </h4>

                {remarkLines.length > 0 ? (
                  <ul className="mkt-remarks-log">
                    {remarkLines.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="mkt-muted">No remarks yet.</p>
                )}

                <div className="mkt-remark-form" onClick={(e) => e.stopPropagation()}>
                  <textarea
                    className="mkt-remark-input"
                    placeholder="Add a remark (e.g. Called, customer wants a callback after 6 PM)"
                    value={remarkText}
                    onChange={(e) => setRemarkText(e.target.value)}
                    rows={4}
                  />
                  <div className="mkt-remark-controls">
                    <label className="stage-label" htmlFor={`status-${lead.id}`}>
                      Follow-up:
                    </label>
                    <select
                      id={`status-${lead.id}`}
                      className="stage-select"
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                    >
                      <option value="">Set status…</option>
                      {FOLLOWUP_STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <button
                      className="mkt-save-btn"
                      onClick={handleSave}
                      disabled={!canSave}
                    >
                      <Save size={14} />
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                  {lead.followup_updated_at && (
                    <p className="mkt-last-updated">
                      Last updated
                      {lead.followup_by ? ` by ${lead.followup_by}` : ''} at{' '}
                      {formatIST(lead.followup_updated_at)} IST
                    </p>
                  )}
                  {saveError && <p className="mkt-save-error">{saveError}</p>}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

interface MarketingLeadsTableProps {
  data: MarketingLeadsResponse | null;
  loading: boolean;
  total: number;
  interest: MarketingInterest;
  includeActioned: boolean;
  stage: string;
  limit: number;
  offset: number;
  startDate: string;
  endDate: string;
  setInterest: (value: MarketingInterest) => void;
  setIncludeActioned: (value: boolean) => void;
  setStage: (value: string) => void;
  setLimit: (value: number) => void;
  setOffset: (value: number) => void;
  onLeadUpdated: (lead: MarketingLead) => void;
}

export default function MarketingLeadsTable({
  data,
  loading,
  total,
  interest,
  includeActioned,
  stage,
  limit,
  offset,
  startDate,
  endDate,
  setInterest,
  setIncludeActioned,
  setStage,
  setLimit,
  setOffset,
  onLeadUpdated,
}: MarketingLeadsTableProps) {
  const [toast, setToast] = useState({ show: false, message: '' });
  const [exporting, setExporting] = useState(false);
  const leads = data?.leads ?? [];

  // Export ALL leads matching the current filters (paged server-side), not
  // just the page on screen.
  const handleExport = async () => {
    setExporting(true);
    try {
      const rows = await fetchAllForExport<MarketingLead>(
        async (page, pageSize) => {
          const res = await fetchMarketingLeads({
            interest,
            include_actioned: includeActioned,
            stage,
            start_date: startDate,
            end_date: endDate,
            limit: pageSize,
            offset: (page - 1) * pageSize,
          });
          return {
            rows: res.leads ?? [],
            totalPages: Math.ceil((res.total ?? 0) / pageSize),
          };
        },
        total || 1,
        EXPORT_PAGE_SIZE
      );

      if (rows.length === 0) {
        setToast({ show: true, message: 'No leads to export' });
        return;
      }

      const exportColumns: ExportColumn<MarketingLead>[] = [
        { key: 'phone_number', label: 'Phone', render: (_v, row) => row.phone_number ?? '' },
        { key: 'stage', label: 'Product', render: (_v, row) => humanizeStage(row.stage) },
        {
          key: 'interest_percentage',
          label: 'Interest %',
          render: (_v, row) => {
            const p = toNumber(row.interest_percentage);
            return p == null ? '' : `${p}%`;
          },
        },
        {
          key: 'call_duration',
          label: 'Duration',
          render: (_v, row) =>
            row.call_duration == null ? '' : formatDuration(row.call_duration),
        },
        { key: 'created_at', label: 'Call Date', render: (_v, row) => formatIST(row.created_at) },
        { key: 'call_status', label: 'Call Status', render: (_v, row) => row.call_status ?? '' },
        { key: 'followup_status', label: 'Follow-up', render: (_v, row) => followupLabel(row.followup_status) },
        {
          key: 'followup_updated_at',
          label: 'Follow-up Updated At',
          render: (_v, row) => (row.followup_updated_at ? `${formatIST(row.followup_updated_at)} IST` : ''),
        },
        { key: 'followup_by', label: 'Follow-up By', render: (_v, row) => row.followup_by ?? '' },
        {
          key: 'transfer_triggered',
          label: 'Transfer Attempted',
          render: (_v, row) => (row.transfer_triggered ? 'Yes' : 'No'),
        },
        { key: 'descriptions', label: 'Remarks', render: (_v, row) => row.descriptions ?? '' },
      ];

      exportToExcel(
        rows as unknown as Record<string, unknown>[],
        exportColumns as unknown as ExportColumn<Record<string, unknown>>[],
        'marketing_leads'
      );
      setToast({ show: true, message: `Exported ${rows.length} leads` });
    } catch (err) {
      setToast({ show: true, message: `Export failed: ${extractApiError(err)}` });
    } finally {
      setExporting(false);
    }
  };

  // Merge known products with any distinct stages present in the current page.
  const productOptions = useMemo(() => {
    const map = new Map<string, string>();
    MARKETING_STAGE_OPTIONS.forEach((o) => map.set(o.value, o.label));
    leads.forEach((l) => {
      if (l.stage && !map.has(l.stage)) map.set(l.stage, humanizeStage(l.stage));
    });
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [leads]);

  // Page-scoped summary numbers (Total is queue-wide from the API).
  const interestedCount = leads.filter((l) => l.is_interested === true).length;
  const contactedCount = leads.filter(
    (l) => l.descriptions != null && l.descriptions.trim() !== ''
  ).length;
  const pctValues = leads
    .map((l) => toNumber(l.interest_percentage))
    .filter((n): n is number => n != null);
  const avgInterest =
    pctValues.length > 0
      ? Math.round(pctValues.reduce((a, b) => a + b, 0) / pctValues.length)
      : 0;

  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, total);
  const canPrev = offset > 0;
  const canNext = offset + limit < total;

  const stats = [
    { label: 'Total Leads', value: total, icon: <Users size={18} />, color: '#2563eb' },
    { label: 'Interested', value: interestedCount, icon: <TrendingUp size={18} />, color: '#16a34a' },
    { label: 'Contacted', value: contactedCount, icon: <CheckCircle2 size={18} />, color: '#0891b2' },
    { label: 'Avg Interest', value: `${avgInterest}%`, icon: <Percent size={18} />, color: '#7c3aed' },
  ];

  return (
    <div className="mkt-wrap">
      <div className="mkt-stats">
        {stats.map((s) => (
          <div className="mkt-stat" key={s.label}>
            <div className="mkt-stat-icon" style={{ backgroundColor: s.color + '18', color: s.color }}>
              {s.icon}
            </div>
            <div className="mkt-stat-info">
              <span className="mkt-stat-value">{s.value}</span>
              <span className="mkt-stat-label">{s.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="table-card">
        <div className="table-header-row">
          <h3 className="table-title">Marketing Leads ({total})</h3>
          <div className="mkt-filters">
            <div className="mkt-seg" role="tablist" aria-label="Interest filter">
              {INTEREST_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="tab"
                  aria-selected={interest === opt.value}
                  className={`mkt-seg-btn ${interest === opt.value ? 'active' : ''}`}
                  onClick={() => setInterest(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="stage-dropdown-wrapper">
              <label className="stage-label">Product:</label>
              <select
                className="stage-select"
                value={stage}
                onChange={(e) => setStage(e.target.value)}
              >
                <option value="">All</option>
                {productOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <label className="mkt-checkbox">
              <input
                type="checkbox"
                checked={includeActioned}
                onChange={(e) => setIncludeActioned(e.target.checked)}
              />
              Show already-contacted
            </label>

            <button
              type="button"
              className="mkt-page-btn mkt-export-btn"
              onClick={handleExport}
              disabled={exporting || loading || total === 0}
              title="Download all leads matching the current filters as Excel"
            >
              <Download size={14} />
              {exporting ? 'Exporting…' : 'Export'}
            </button>
          </div>
        </div>

        <div className={`table-wrapper ${loading ? 'mkt-loading' : ''}`}>
          {leads.length === 0 ? (
            <p className="no-data">
              No leads match these filters. Try widening the date range or switching Interest to “All”.
            </p>
          ) : (
            <table className="calls-table">
              <thead>
                <tr>
                  <th style={{ width: 32 }}></th>
                  <th>Phone</th>
                  <th>Product</th>
                  <th>Interest %</th>
                  <th>Duration</th>
                  <th>Call Date</th>
                  <th>Follow-up</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <MarketingLeadRow
                    key={lead.id}
                    lead={lead}
                    onLeadUpdated={onLeadUpdated}
                    notify={(message) => setToast({ show: true, message })}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="mkt-pagination">
          <div className="mkt-page-info">
            {total === 0 ? 'No results' : `Showing ${from}–${to} of ${total}`}
          </div>
          <div className="mkt-page-controls">
            <label className="stage-label">Rows:</label>
            <select
              className="stage-select"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <button
              className="mkt-page-btn"
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={!canPrev || loading}
            >
              <ChevronLeft size={16} />
              Prev
            </button>
            <button
              className="mkt-page-btn"
              onClick={() => setOffset(offset + limit)}
              disabled={!canNext || loading}
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      <Toast
        show={toast.show}
        message={toast.message}
        onClose={() => setToast({ show: false, message: '' })}
      />
    </div>
  );
}
